// src/views/Orchestration/WhatsHappening/AuxComponents/AIAssistant.js
/* eslint-disable */
import React, { useState, useRef, useEffect, useContext } from "react";
import Draggable from "react-draggable";
import {
  IconButton,
  Paper,
  Typography,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot } from "@fortawesome/free-solid-svg-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import { generateInsight } from "services/aiApi.js";
import AuthContext from "contexts/AuthProvider.js";

const PROMPT_OPTIONS = [
  { key: "executive_briefing", label: "Executive Briefing" },
  // { key: "at_risk_initiatives", label: "At-Risk Initiatives Details" },
  // { key: "program_risks", label: "Program Risks" },
  { key: "kpi_summary", label: "KPIs Summary" },
];

const AIAssistant = ({ classes, clientColor, isOpen, onToggle, isFullScreen = false }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);
  const nodeRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    if (isOpen) {
      const welcomeMessage =
        t("whatshappening.ai_assistant.welcome") ||
        "Welcome! How can I assist you?";
      const initialMessages = [
        { sender: "ai", text: welcomeMessage, success: true },
      ];
      setMessages(initialMessages);
    }
  }, [isOpen]);

  const handlePromptClick = async (prompt) => {
    setMessages((prev) => [
      ...prev,
      {
        sender: "user",
        text: t(`whatshappening.ai_assistant.prompts.${prompt.key}`),
      },
    ]);
    setIsLoading(true);

    const response = await generateInsight(prompt.key);

    setMessages((prev) => [
      ...prev,
      { sender: "ai", text: response.text, success: response.success },
    ]);
    setIsLoading(false);
  };

  const fab = (
    <Draggable
      grid={[1, 1]}
      onStart={() => setIsDragging(false)}
      onDrag={() => setIsDragging(true)}
      onStop={() => {
        if (!isDragging) {
          onToggle();
        }
      }}
    >
              <div
          style={{
            position: "fixed",
            right: 24,
            bottom: 24,
            // IMPORTANTE: zIndex deve ser maior que o fullScreenContainer (9999)
            // para aparecer corretamente no modo full screen
            // No modo full screen, o AIAssistant não deve aparecer
            zIndex: isFullScreen ? -1 : 10000,
            touchAction: "none",
          }}
        >
        <IconButton
          style={{
            background: clientColor,
            color: "#fff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
            width: 56,
            height: 56,
          }}
        >
          <FontAwesomeIcon icon={faRobot} />
        </IconButton>
      </div>
    </Draggable>
  );

  if (!isOpen) {
    return fab;
  }

  return (
    <>
      {fab}
      <Draggable handle=".drag-bar" position={null} nodeRef={nodeRef}>
        <Paper
          ref={nodeRef}
          elevation={8}
          style={{
            position: "fixed",
            right: "24px",
            bottom: "96px",
            width: 400,
            height: 550,
            display: "flex",
            flexDirection: "column",
            // IMPORTANTE: zIndex deve ser maior que o fullScreenContainer (9999)
            // para aparecer corretamente no modo full screen
            // No modo full screen, o AIAssistant não deve aparecer
            zIndex: isFullScreen ? -1 : 10000,
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <div
            className="drag-bar"
            style={{
              cursor: "move",
              background: clientColor,
              color: "#fff",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h5" style={{ fontSize: "1rem" }}>
              {t("whatshappening.ai_assistant.title")}
            </Typography>
            <IconButton
              size="small"
              style={{ color: "#fff" }}
              onClick={onToggle}
            >
              <Close fontSize="small" />
            </IconButton>
          </div>

          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "12px",
              backgroundColor: "#f9f9f9",
            }}
          >
            {messages.map((msg, index) => (
              <div
                key={index}
                style={{
                  marginBottom: "12px",
                  display: "flex",
                  flexDirection: msg.sender === "user" ? "row-reverse" : "row",
                }}
              >
                <Paper
                  elevation={1}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "10px",
                    backgroundColor:
                      msg.sender === "user" ? clientColor : "#fff",
                    color: msg.sender === "user" ? "#fff" : "#333",
                    maxWidth: "85%",
                    border: msg.success === false ? "1px solid red" : "none",
                  }}
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ node, ...props }) => (
                        <Typography
                          variant="body1"
                          style={{ fontWeight: "bold", marginBottom: "6px" }}
                          {...props}
                        />
                      ),
                      h2: ({ node, ...props }) => (
                        <Typography
                          variant="body1"
                          style={{ fontWeight: "bold", marginBottom: "6px" }}
                          {...props}
                        />
                      ),
                      h3: ({ node, ...props }) => (
                        <Typography
                          variant="body1"
                          style={{ fontWeight: "bold", marginBottom: "6px" }}
                          {...props}
                        />
                      ),
                      h4: ({ node, ...props }) => (
                        <Typography
                          variant="body1"
                          style={{ fontWeight: "bold", marginBottom: "6px" }}
                          {...props}
                        />
                      ),
                      p: ({ node, ...props }) => (
                        <Typography
                          variant="body2"
                          style={{ margin: 0, marginBottom: "8px" }}
                          {...props}
                        />
                      ),
                      strong: ({ node, ...props }) => (
                        <strong style={{ fontWeight: 600 }} {...props} />
                      ),
                      li: ({ node, ...props }) => (
                        <li
                          style={{ marginBottom: "4px", marginLeft: "16px" }}
                          {...props}
                        />
                      ),
                    }}
                  >
                    {msg.text}
                  </ReactMarkdown>
                </Paper>
              </div>
            ))}
            {isLoading && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <CircularProgress size={24} style={{ color: clientColor }} />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div
            style={{
              padding: "12px",
              borderTop: "1px solid #eee",
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            {PROMPT_OPTIONS.map((prompt) => (
              <Button
                key={prompt.key}
                variant="outlined"
                size="small"
                disabled={isLoading}
                onClick={() => handlePromptClick(prompt)}
                style={{
                  borderColor: clientColor,
                  color: clientColor,
                  textTransform: "none",
                  borderRadius: "16px",
                }}
              >
                {t(`whatshappening.ai_assistant.prompts.${prompt.key}`)}
              </Button>
            ))}
          </div>
        </Paper>
      </Draggable>
    </>
  );
};

AIAssistant.propTypes = {
  classes: PropTypes.object.isRequired,
  clientColor: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  isFullScreen: PropTypes.bool,
};

export default AIAssistant;
